import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { errorMessageKey, unwrap } from '../../api/api-error';
import { ensureOk, useMe } from '../../auth/session';
import { Button } from '../../components/ui/button';
import { Avatar } from '../../components/ui/avatar';
import { Card } from '../../components/ui/card';
import { Notice } from '../../components/ui/notice';
import { useI18n } from '../../i18n/i18n';
import { can, type WorkspaceRole } from '../../workspaces/workspaces-api';
import { RoleBadge } from './role-badge';
import { useCurrentWorkspace } from '../../workspaces/current-workspace';
import { Select } from '../../components/ui/select';

const ROLES: readonly WorkspaceRole[] = ['owner', 'member'];

/** Участники: список; владельцу — смена роли и удаление. */
export function MembersPage() {
  const { t } = useI18n();
  const workspace = useCurrentWorkspace();
  const me = useMe();
  const queryClient = useQueryClient();
  const key = ['workspaces', workspace.id, 'members'];
  const members = useInfiniteQuery({
    queryKey: key,
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) =>
      unwrap(
        await api.GET('/api/v1/workspaces/{workspaceId}/members', {
          params: {
            path: { workspaceId: workspace.id },
            query: pageParam === undefined ? {} : { cursor: pageParam },
          },
        }),
      ),
    getNextPageParam: (page) => page.nextCursor ?? undefined,
  });
  const refresh = () => queryClient.invalidateQueries({ queryKey: key });
  const changeRole = useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: WorkspaceRole }) =>
      unwrap(
        await api.PATCH('/api/v1/workspaces/{workspaceId}/members/{userId}', {
          params: { path: { workspaceId: workspace.id, userId } },
          body: { role },
        }),
      ),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: async (userId: string) =>
      ensureOk(
        await api.DELETE('/api/v1/workspaces/{workspaceId}/members/{userId}', {
          params: { path: { workspaceId: workspace.id, userId } },
        }),
      ),
    onSuccess: refresh,
  });
  const manage = can(workspace, 'members.manage');
  const items = members.data?.pages.flatMap((page) => page.items) ?? [];
  const error = changeRole.error ?? remove.error ?? members.error;

  return (
    <Card title={t('workspace.tab.members')}>
      <ul className="-my-2 divide-y divide-border text-sm">
        {items.map((member) => (
          <li key={member.userId} className="flex items-center justify-between gap-4 py-2">
            <div className="flex min-w-0 items-center gap-3">
              <Avatar
                name={member.name}
                {...(member.email !== null ? { email: member.email } : {})}
                src={member.avatarUrl}
              />
              <div className="min-w-0">
                <p className="truncate">
                  {member.name ?? t('common.formerUser')}
                  {member.userId === me.data?.id && (
                    <span className="text-muted-foreground"> · {t('members.you')}</span>
                  )}
                </p>
                <p className="truncate text-2xs text-muted-foreground">{member.email ?? '—'}</p>
              </div>
            </div>
            {manage ? (
              <span className="flex items-center gap-2">
                <Select<WorkspaceRole>
                  aria-label={t('members.role')}
                  value={member.role}
                  disabled={changeRole.isPending}
                  onValueChange={(role) => changeRole.mutate({ userId: member.userId, role })}
                  options={ROLES.map((role) => ({
                    value: role,
                    label: t(`workspaces.role.${role}`),
                  }))}
                  className="min-w-32"
                />
                {member.userId !== me.data?.id && (
                  <Button
                    type="button"
                    variant="link"
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(member.userId)}
                  >
                    {t('members.remove')}
                  </Button>
                )}
              </span>
            ) : (
              <RoleBadge role={member.role} />
            )}
          </li>
        ))}
      </ul>
      {members.hasNextPage && (
        <Button type="button" variant="secondary" onClick={() => void members.fetchNextPage()}>
          {t('common.showMore')}
        </Button>
      )}
      {error !== null && <Notice tone="danger">{t(errorMessageKey(error))}</Notice>}
    </Card>
  );
}
